import React, { useMemo, useState } from 'react';
import { Card, Col, Input, Row, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import {
  accessInventoryRoutes,
  accessRoleColors,
  accessRoleLabels,
} from '../../constants/accessInventory';
import type { AccessInventoryRoute, AccessRole } from '../../constants/accessInventory';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

type ApiInventoryPage = {
  key: string;
  title: string;
  path: string;
};

type ApiInventoryRow = {
  apiGroup: string;
  pages: ApiInventoryPage[];
  roles: AccessRole[];
  note: string;
};

const roleOptions = (Object.keys(accessRoleLabels) as AccessRole[]).map((role) => ({
  label: accessRoleLabels[role],
  value: role,
}));

const normalizeText = (value?: string) => (value || '').toLocaleLowerCase('tr-TR').trim();

const includesSearch = (searchTerm: string, values: Array<string | undefined>) => {
  const normalizedSearch = normalizeText(searchTerm);

  if (!normalizedSearch) {
    return true;
  }

  return values.some((value) => normalizeText(value).includes(normalizedSearch));
};

const hasSelectedRole = (route: AccessInventoryRoute, selectedRoles: AccessRole[]) => {
  if (selectedRoles.length === 0) {
    return true;
  }

  return selectedRoles.some((role) => route.roles.includes(role));
};

const renderRoles = (roles: AccessRole[]) => (
  <Space size={[4, 4]} wrap>
    {roles.map((role) => (
      <Tag key={role} color={accessRoleColors[role]}>
        {accessRoleLabels[role]}
      </Tag>
    ))}
  </Space>
);

const renderApiGroups = (apiGroups: string[]) => {
  if (apiGroups.length === 0) {
    return <Text type="secondary">API yok</Text>;
  }

  return (
    <Space size={[4, 4]} wrap>
      {apiGroups.map((apiGroup) => (
        <Tag key={apiGroup}>{apiGroup}</Tag>
      ))}
    </Space>
  );
};

const buildApiRows = (routes: AccessInventoryRoute[]): ApiInventoryRow[] => {
  const grouped = new Map<
    string,
    {
      pages: Map<string, ApiInventoryPage>;
      roles: Set<AccessRole>;
      hasPublicRoute: boolean;
    }
  >();

  routes.forEach((route) => {
    route.apiGroups.forEach((apiGroup) => {
      if (!grouped.has(apiGroup)) {
        grouped.set(apiGroup, {
          pages: new Map(),
          roles: new Set(),
          hasPublicRoute: false,
        });
      }

      const item = grouped.get(apiGroup);

      if (!item) {
        return;
      }

      item.pages.set(route.id, {
        key: route.id,
        title: route.title,
        path: route.path,
      });

      route.roles.forEach((role) => item.roles.add(role));
      item.hasPublicRoute = item.hasPublicRoute || route.roles.includes('PUBLIC');
    });
  });

  return Array.from(grouped.entries())
    .map(([apiGroup, item]) => {
      const pages = Array.from(item.pages.values()).sort((first, second) => first.title.localeCompare(second.title, 'tr-TR'));
      const roles = Array.from(item.roles).sort((first, second) => accessRoleLabels[first].localeCompare(accessRoleLabels[second], 'tr-TR'));
      const noteParts = [`${pages.length} URL tarafından kullanılıyor.`];

      if (item.hasPublicRoute) {
        noteParts.push('Public erişim içerir.');
      }

      return {
        apiGroup,
        pages,
        roles,
        note: noteParts.join(' '),
      };
    })
    .sort((first, second) => first.apiGroup.localeCompare(second.apiGroup, 'tr-TR'));
};

const AccessInventory: React.FC = () => {
  const [selectedRoles, setSelectedRoles] = useState<AccessRole[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const roleFilteredRoutes = useMemo(
    () => accessInventoryRoutes.filter((route) => hasSelectedRole(route, selectedRoles)),
    [selectedRoles],
  );

  const filteredUrlRoutes = useMemo(
    () =>
      roleFilteredRoutes.filter((route) =>
        includesSearch(searchTerm, [
          route.title,
          route.path,
          route.permission,
          route.note,
          route.apiGroups.join(' '),
          route.roles.map((role) => accessRoleLabels[role]).join(' '),
        ]),
      ),
    [roleFilteredRoutes, searchTerm],
  );

  const apiRows = useMemo(() => buildApiRows(roleFilteredRoutes), [roleFilteredRoutes]);

  const filteredApiRows = useMemo(
    () =>
      apiRows.filter((row) =>
        includesSearch(searchTerm, [
          row.apiGroup,
          row.note,
          row.pages.map((page) => `${page.title} ${page.path}`).join(' '),
          row.roles.map((role) => accessRoleLabels[role]).join(' '),
        ]),
      ),
    [apiRows, searchTerm],
  );

  const urlColumns: ColumnsType<AccessInventoryRoute> = [
    {
      title: 'Sayfa',
      dataIndex: 'title',
      key: 'title',
      width: 220,
      render: (title: string) => <Text strong>{title}</Text>,
    },
    {
      title: 'URL',
      dataIndex: 'path',
      key: 'path',
      width: 260,
      render: (path: string) => <Text code>{path}</Text>,
    },
    {
      title: 'Erişen Roller',
      dataIndex: 'roles',
      key: 'roles',
      width: 260,
      render: (roles: AccessRole[]) => renderRoles(roles),
    },
    {
      title: 'İzin',
      dataIndex: 'permission',
      key: 'permission',
      width: 150,
      render: (permission?: string) => (permission ? <Text code>{permission}</Text> : <Text type="secondary">-</Text>),
    },
    {
      title: 'API Grupları',
      dataIndex: 'apiGroups',
      key: 'apiGroups',
      render: (apiGroups: string[]) => renderApiGroups(apiGroups),
    },
    {
      title: 'Not',
      key: 'note',
      width: 260,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text type={record.menuVisible ? 'success' : 'secondary'}>
            {record.menuVisible ? 'Menüde görünür' : 'Detay/teknik route'}
          </Text>
          {record.note && <Text type="secondary">{record.note}</Text>}
        </Space>
      ),
    },
  ];

  const apiColumns: ColumnsType<ApiInventoryRow> = [
    {
      title: 'API Grubu',
      dataIndex: 'apiGroup',
      key: 'apiGroup',
      width: 260,
      render: (apiGroup: string) => <Text code>{apiGroup}</Text>,
    },
    {
      title: 'Kullanan Sayfalar',
      dataIndex: 'pages',
      key: 'pages',
      render: (pages: ApiInventoryPage[]) => (
        <Space direction="vertical" size={3}>
          {pages.slice(0, 6).map((page) => (
            <span key={page.key}>
              <Text strong>{page.title}</Text> <Text type="secondary">{page.path}</Text>
            </span>
          ))}
          {pages.length > 6 && <Text type="secondary">+{pages.length - 6} sayfa daha</Text>}
        </Space>
      ),
    },
    {
      title: 'Roller',
      dataIndex: 'roles',
      key: 'roles',
      width: 260,
      render: (roles: AccessRole[]) => renderRoles(roles),
    },
    {
      title: 'Not',
      dataIndex: 'note',
      key: 'note',
      width: 220,
      render: (note: string) => <Text type="secondary">{note}</Text>,
    },
  ];

  return (
    <Card className="br-30px">
      <Space direction="vertical" size={18} style={{ width: '100%' }}>
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Erişim Envanteri
            </Title>
            <Text type="secondary">
              Frontend route envanteri ve bu route'ların kullandığı API grupları
            </Text>
          </Col>
          <Col>
            <Space size={16} wrap>
              <Text>
                <strong>{filteredUrlRoutes.length}</strong> URL
              </Text>
              <Text>
                <strong>{filteredApiRows.length}</strong> API grubu
              </Text>
              <Text type="secondary">
                Toplam {accessInventoryRoutes.length} URL
              </Text>
            </Space>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col xs={24} md={12} lg={9}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Role göre filtrele"
              value={selectedRoles}
              options={roleOptions}
              onChange={(value) => setSelectedRoles(value as AccessRole[])}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={12} lg={9}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              placeholder="Sayfa, URL, izin veya API ara"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </Col>
        </Row>

        <Tabs defaultActiveKey="urls" destroyInactiveTabPane>
          <TabPane tab="URL Bazlı" key="urls">
            <Table
              rowKey="id"
              columns={urlColumns}
              dataSource={filteredUrlRoutes}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 1200 }}
            />
          </TabPane>
          <TabPane tab="API Bazlı" key="apis">
            <Table
              rowKey="apiGroup"
              columns={apiColumns}
              dataSource={filteredApiRows}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 1000 }}
            />
          </TabPane>
        </Tabs>
      </Space>
    </Card>
  );
};

export default AccessInventory;
